"use client";
import Link from "next/link";
import Image from "next/image";
import { useState } from "react";

import { navItems, notificationPage, adminPage } from "@/constants/page";
import { useIsLg } from "@/hooks/useIsLg";
import defaultProfile from "@/images/default-profile.jpg";
import { NavItem } from "@/types";
import { X } from "lucide-react";
import { motion } from "framer-motion";
import { ModeToggle } from "./theme-btn";
import { useRouter, usePathname } from "next/navigation";
import { useAuth } from "@/contexts/authContext";
import LogoutButton from "@/components/ui/logOutButton";
import { useSelector } from "react-redux";
import { RootState } from "@/lib/features/store";

const TopNav = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const isLg = useIsLg();
  const router = useRouter();
  const pathname = usePathname();
  const { user, logout, isAuthenticated } = useAuth();

  // Create nav items array with conditional admin button
  const typedNavItems: NavItem[] = [
    ...navItems,
    ...(user?.isAdmin ? [adminPage] : []),
  ];
  const unreadCount = useSelector(
    (state: RootState) => state.notification.count
  );
  const chatCount = useSelector(
    (state: RootState) => state.notification.chatCount
  );

  const handleLogOut = async () => {
    try {
      await logout();
      router.push("/login");
    } catch (error) {
      // handle error if needed
    }
  };

  const normalizePath = (path: string) => {
    const normalized = path.replace(/\/+$/, "");
    return normalized === "" ? "/" : normalized;
  };

  const currentPath = pathname ? normalizePath(pathname) : "";
  if (pathname.includes("/clips") || pathname.includes("/chats/")) return null;
  if (!isAuthenticated) return null;
  return (
    <div
      className={`md:hidden max-w-screen-xl  top-0 sticky  inset-0 ${isSidebarOpen ? "z-50" : "z-10"}`}
    >
      <div className="flex justify-between items-center md:p-4 p-1 bg-black">
        <Link href="/">
          <div className=" flex items-center justify-center rounded-full ">
            <Image
              src="/public/Logo_SocialGraphs.png"
              alt="SocialGraphs Logo"
              width={50}
              height={50}
              className="rounded-full object-cover"
            />
          </div>
        </Link>
        {/* Profile Image to Toggle Sidebar */}
        <button onClick={() => setIsSidebarOpen(true)} data-cy="top-nav-btn">
          <Image
            src={
              user?.profile_picture?.thumbnail_url ||
              user?.profile_picture?.url ||
              defaultProfile.src
            }
            alt="Profile"
            width={40}
            height={40}
            className="rounded-full cursor-pointer"
          />
        </button>

        {/* Sidebar */}
        {isSidebarOpen && (
          <motion.div
            className="fixed inset-0 bg-transparent shadow-secondary-foreground  z-50 "
            onClick={() => setIsSidebarOpen(false)}
          >
            <div
              className="fixed right-0 top-0 w-64 h-full theme-layout shadow-lg p-5 rounded-xl backdrop-blur-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Sidebar Header */}
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <Image
                    src={user?.profile_picture?.url || defaultProfile.src}
                    alt="Profile"
                    width={40}
                    height={40}
                    className="rounded-full"
                  />
                  <div>
                    <span className="text-sm font-bold">
                      {user?.display_name}
                    </span>
                    <span className="block text-xs text-gray-400">
                      @{user?.name}
                    </span>
                  </div>
                </div>
                <button onClick={() => setIsSidebarOpen(false)}>
                  <X className="w-6 h-6 dark:text-gray-300 hover:text-white text-black" />
                </button>
              </div>

              {/* Sidebar Navigation */}
              <nav className="flex-1 overflow-y-auto">
                <ul className="space-y-1 p-2">
                  {typedNavItems.map((item) => {
                    const isProfile = item.name === "Profile";
                    const href =
                      isProfile && user?.name
                        ? `/profile/${user.name}`
                        : item.toLg && isLg
                          ? item.toLg
                          : item.to;
                    const itemPath = normalizePath(href);
                    const isActive =
                      itemPath === "/"
                        ? currentPath === "/"
                        : currentPath === itemPath ||
                          currentPath.startsWith(`${itemPath}/`);

                    return (
                      <li
                        key={item.to}
                        className="rounded-lg bg-gray-50/50 dark:bg-gray-500/10  "
                      >
                        <Link
                          href={href}
                          onClick={() => setIsSidebarOpen(false)}
                          data-cy={`sidebar-link-${item.name}`}
                          className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                            isActive
                              ? "bg-primary text-primary-foreground font-semibold"
                              : "hover:bg-secondary"
                          }`}
                        >
                          <div className="relative">
                            {item.icon && <item.icon className="h-6 w-6" />}
                            {item.to === notificationPage.to &&
                              unreadCount > 0 && (
                                <span className="absolute -top-1 -right-1 bg-gradient-to-r from-[#e0aa3e] to-[#f9f295] text-black text-xs rounded-full h-4 w-4 flex items-center justify-center">
                                  {unreadCount}
                                </span>
                              )}
                          </div>
                          <span className="font-medium">{item.name}</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </nav>
              <div className="p-2">
                <div className="rounded-lg bg-red-700 dark:bg-red-800  w-full">
                  <LogoutButton />
                </div>
              </div>
              <div className="mt-4 flex justify-center ">
                <ModeToggle data-cy="theme-toggle" />
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};
export default TopNav;
